import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Bearer-token fallback for cross-site cookie blocking.
 *
 * Born Day is served from `bornday.me` but the FastAPI backend lives on
 * `*.emergent.host`. They're different registrable domains, which makes
 * the `session_token` cookie a *third-party* cookie from the browser's
 * perspective. Safari ITP blocks third-party cookies by default; Brave
 * blocks them too; Chrome is in the middle of phasing them out. The
 * symptom: the user clicks "Continue with Google", authenticates fine,
 * but the cookie set by the backend gets silently dropped — every
 * subsequent /api/auth/me call returns 401 and the app loops back to
 * /login. (Reported by Steve in production, Feb 2026.)
 *
 * Fix without touching DNS: also keep the session_token in localStorage
 * and send it as `Authorization: Bearer <token>` on every request. The
 * backend already prefers the cookie but falls back to the Bearer
 * header (see `get_current_user` in server.py). Browsers that DO allow
 * the cookie still use it; browsers that block it now have a working
 * second channel.
 *
 * httpOnly cookies remain the primary mechanism — this header is an
 * additive belt-and-suspenders, not a replacement.
 */
const TOKEN_KEY = "bd_session_token";

export function getSessionToken() {
  try { return window.localStorage?.getItem(TOKEN_KEY) || null; }
  catch (e) { return null; }
}

export function setSessionToken(t) {
  try {
    if (t) window.localStorage?.setItem(TOKEN_KEY, t);
    else window.localStorage?.removeItem(TOKEN_KEY);
  } catch (e) { /* private-mode storage failure — safe to ignore */ }
}

export function clearSessionToken() { setSessionToken(null); }

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const t = getSessionToken();
  if (t) {
    config.headers = config.headers || {};
    // Don't clobber an explicit Authorization a caller already set.
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

export const formatError = (e) => {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  return String(d);
};

export default api;
